import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Check, Copy } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export interface OneTimeSecret {
  clientName: string
  token: string
}

interface OneTimeSecretDialogProps {
  /** Null closes the dialog. The parent drops its copy on close, so the token is gone after. */
  secret: OneTimeSecret | null
  onClose: () => void
}

/**
 * Shows a freshly issued API-client token exactly once.
 *
 * Guarded against losing it by accident: no close cross, no close on outside click or Escape —
 * only the explicit "gespeichert" button closes it. The token lives in this dialog's props only; it
 * is never written to storage, a query cache or a toast.
 */
export function OneTimeSecretDialog({
  secret,
  onClose,
}: OneTimeSecretDialogProps) {
  const { t } = useTranslation('integrations')
  const [copied, setCopied] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setCopied(false)
  }, [secret?.token])

  async function copy() {
    if (!secret) return
    try {
      await navigator.clipboard.writeText(secret.token)
      setCopied(true)
      toast.success(t('secret.copied'))
    } catch {
      // Clipboard blocked (permissions, insecure context): select the text so Cmd/Ctrl+C works.
      inputRef.current?.select()
      toast.error(t('secret.copyFailed'))
    }
  }

  return (
    <Dialog open={secret != null}>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        className="sm:max-w-xl"
      >
        <DialogHeader>
          <DialogTitle>
            {t('secret.title', { name: secret?.clientName ?? '' })}
          </DialogTitle>
          <DialogDescription>{t('secret.description')}</DialogDescription>
        </DialogHeader>

        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('secret.onceTitle')}</AlertTitle>
          <AlertDescription>{t('secret.onceMessage')}</AlertDescription>
        </Alert>

        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            readOnly
            value={secret?.token ?? ''}
            aria-label={t('secret.tokenLabel')}
            spellCheck={false}
            autoComplete="off"
            onFocus={(event) => event.currentTarget.select()}
            className="border-input bg-muted min-w-0 flex-1 rounded-md border px-3 py-2 font-mono text-sm"
          />
          <Button type="button" variant="outline" onClick={() => void copy()}>
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            <span className="ml-2">
              {copied ? t('secret.copiedShort') : t('secret.copy')}
            </span>
          </Button>
        </div>

        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {t('secret.done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
